import { Outlet } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useUI } from '../../context/UIProvider';
import FloatingSidebar, { LinkDef } from '../sidebar/FloatingSidebar';
import { useEffect, useState } from 'react';

/* Remove the default body margin that creates a white border,
   and ensure the root can stretch full height */
const ViewportReset = createGlobalStyle`
  html, body, #root { height: 100%; }
  body { margin: 0; background: transparent; }
`;

const Shell = styled.div`
  position: relative;
  isolation: isolate;
  width: 100%;
  height: 100dvh;
  @supports not (height: 100dvh) { height: 100vh; }
  overflow: hidden;
`;


/* Full-viewport background behind sidebar + main.
   Use a CSS var for easy theming; fallback is a dark gradient. */
const Background = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  background: var(--dashboard-bg,
    radial-gradient(1200px 700px at 12% -12%, rgba(224, 161, 94, .14), rgba(224, 161, 94, 0) 58%),
    radial-gradient(1000px 560px at 100% 2%, rgba(166, 76, 68, .16), rgba(166, 76, 68, 0) 60%),
    linear-gradient(180deg, #171822 0%, #12131c 48%, #0f1016 100%)
  );
  background-attachment: fixed;
`;

const SidebarWrapper = styled.div<{ $collapsed: boolean }>`
  position: fixed;
  top: 1rem;
  left: 1rem;
  z-index: 100;
  
  @media (max-width: 767px) {
    position: static; /* Let the mobile sidebar handle its own positioning */
  }
`;

const Main = styled.main<{ $sidebarCollapsed: boolean }>`
position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  // padding: 2rem;
  box-sizing: border-box;
  
  /* Add padding to account for the floating sidebar */
  @media (min-width: 768px) {
     /* Reserve space equal to: left margin (1rem) + sidebar width + right gutter (1rem) */
    padding-left: calc(1rem + var(--sb-w, 220px) + 1rem);
    padding-right: 1.25rem;
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
    transition: padding-left 0.25s ease;
  }
  
  @media (max-width: 767px) {
    padding: 1rem;
    padding-top: 4rem; /* Space for hamburger button */
  }
`;

const Content = styled.div`
  width: 100%;
  margin: 0 auto;
`;

export default function DashboardShell({ 
  links, 
  createTicketLink,
  mobileSidebarWidth,
}: { 
  links: LinkDef[]; 
  createTicketLink?: LinkDef & { to: string };  // Note the ? making it optional
  mobileSidebarWidth?: string;
}) {
  const { sidebarOpen } = useUI();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  /* Sidebar widths must match FloatingSidebar desktop widths:
     - collapsed: 56px  (see Container width in FloatingSidebar)
     - expanded:  220px */
  const sidebarWidth = sidebarOpen ? 220 : 56;

  /* Optional: prevent background scroll when the mobile drawer is open */
  useEffect(() => {
    if (mobileMenuOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileMenuOpen]);

  return (
    <Shell style={{ ['--sb-w' as any]: `${sidebarWidth}px` }}>
      <ViewportReset />
      <Background />
      <SidebarWrapper $collapsed={!sidebarOpen}>
        <FloatingSidebar 
          links={links} 
          collapsed={!sidebarOpen}
          mobileOpen={mobileMenuOpen}
          onMobileToggle={toggleMobileMenu}
          mobileWidth={mobileSidebarWidth}
        />
      </SidebarWrapper>

      <Main $sidebarCollapsed={!sidebarOpen}>
        <Content>
          <Outlet />
        </Content>
      </Main>
    </Shell>
  );
}
